import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  useMediaQuery,
  Button,
  Modal,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Grid,
  Autocomplete,
  TextField,
  Collapse,
  Checkbox,
  Drawer,
  Tooltip,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import {
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { scheduleFollowUpReminder } from "../../utils/reminder";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import LeadCountCards from "./LeadCountCards";
import CallIcon from "@mui/icons-material/Call";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LeadTransferModal from "./LeadTransferModal";
import ErrorModal from "../../utils/ErrorModal";
import LeadDataTable from "./LeadDataTable";
import { useOrganization } from "../../utils/OrganizationContext";
import Chip from "@mui/material/Chip";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ThemedDatePicker from "../../utils/ThemedDatePicker";
import ThemedTimePicker from "../../utils/ThemedTimePicker";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";
const WA = "#1B6B41";

// Roles whose contact details are masked — no call / WhatsApp for them.
const NO_CONTACT_ROLES = ["VT1", "VT2", "PARTNER"];

// Scoped theme: restyles this screen's MUI buttons, inputs, menus, chips and
// autocompletes without touching the component tree.
const tableTheme = createTheme({
  palette: {
    primary: { main: PRIMARY, dark: "#084767", contrastText: "#FFFFFF" },
    success: { main: "#2FA864", dark: WA, contrastText: "#FFFFFF" },
    text: { primary: INK, secondary: "#4A5A6E" },
    divider: "#EDF1F5",
  },
  shape: { borderRadius: 10 },
  typography: { fontFamily: FONT, button: { textTransform: "none", fontWeight: 600 } },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 9, fontFamily: FONT, fontWeight: 600 },
        sizeSmall: { minHeight: 30, fontSize: 12.5, paddingInline: 10, borderRadius: 8 },
        contained: { boxShadow: "none", "&:hover": { boxShadow: "none" } },
        outlined: { borderColor: FIELD_EDGE, background: "#FFFFFF", color: INK, "&:hover": { background: "#F4F7FA", borderColor: "#B4C6DA" } },
        outlinedSuccess: { borderColor: FIELD_EDGE, color: INK, "&:hover": { background: "#EAF2FB", borderColor: "#8FC3E6", color: "#1B4F82" } },
        text: { color: PRIMARY, "&:hover": { background: "#EAF2FB" } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          background: "#FFFFFF",
          fontFamily: FONT,
          fontSize: 14,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#B4C6DA" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY, borderWidth: 1 },
          "& input[type=number]": { fontFamily: MONO, fontSize: 13 },
        },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { fontFamily: FONT, fontSize: 14, color: MUTED, "&.Mui-focused": { color: PRIMARY } } } },
    MuiMenu: {
      styleOverrides: {
        paper: { marginTop: 4, borderRadius: 12, border: `1px solid ${LINE}`, boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)" },
        list: { padding: 4 },
      },
    },
    MuiMenuItem: { styleOverrides: { root: { fontFamily: FONT, fontSize: 13.5, borderRadius: 8, minHeight: 36 } } },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { marginTop: 4, borderRadius: 12, border: `1px solid ${LINE}`, boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)" },
        option: { fontFamily: FONT, fontSize: 13.5, borderRadius: 8, marginInline: 6 },
        groupLabel: { fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: MUTED, lineHeight: "30px" },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontFamily: FONT, fontWeight: 600, fontSize: 12, background: "#EAF2FB", border: "1px solid #C9DEF3", color: "#1B4F82" },
        deleteIcon: { color: "#1B4F82", opacity: 0.6, "&:hover": { color: "#1B4F82", opacity: 1 } },
      },
    },
    MuiCheckbox: { styleOverrides: { root: { color: "#C3CDD9", "&.Mui-checked": { color: PRIMARY } } } },
    MuiBackdrop: { styleOverrides: { root: { "&:not(.MuiBackdrop-invisible)": { background: "rgba(15,27,42,0.46)" } } } },
    MuiSkeleton: { styleOverrides: { root: { background: "#EDF1F5" } } },
  },
});

const labelStyle = {
  display: "block",
  margin: "0 0 6px",
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 600,
  color: INK_2,
};
const modalBoxSx = (isMobile) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: isMobile ? "90%" : 440,
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "#FFFFFF",
  border: 0,
  borderRadius: "18px",
  boxShadow: "0 32px 64px -20px rgba(15,27,42,0.5)",
  p: isMobile ? 2 : 3,
  outline: "none",
  fontFamily: FONT,
  color: INK,
});
const modalTitleStyle = {
  margin: "0 0 14px",
  fontFamily: FONT,
  fontSize: 18,
  fontWeight: 800,
  letterSpacing: "-0.02em",
  color: INK,
};
const waBtnSx = {
  background: "#E9F7EE",
  color: WA,
  border: "1px solid #C4E8D2",
  height: 32,
  px: 0.75,
  borderRadius: "9px",
  "&:hover": { backgroundColor: "#DCF1E4" },
  "&:active": { backgroundColor: "#DCF1E4" },
  "&:focus": { backgroundColor: "#DCF1E4" },
};
const callBtnSx = {
  background: "#EAF2FB",
  color: PRIMARY,
  border: "1px solid #C9DEF3",
  width: 32,
  height: 32,
  borderRadius: "9px",
  "&:hover": { backgroundColor: "#DDEBF8" },
};
const bellBtnSx = {
  background: "#FDF4E1",
  color: "#8A5A08",
  border: "1px solid #F3E0B4",
  width: 32,
  height: 32,
  borderRadius: "9px",
  "&:hover": { backgroundColor: "#FAEBCB" },
  "&.Mui-disabled": { opacity: 0.45, color: "#8A5A08" },
};
const waMenuPaper = {
  boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)",
  border: `1px solid ${LINE}`,
  borderRadius: "12px",
  mt: 1,
  minWidth: 200,
};
const waItemSx = {
  fontFamily: FONT,
  fontSize: "13px",
  padding: "7px 10px",
  borderRadius: "8px",
  mx: 0.5,
  my: 0.3,
  "&:hover": { backgroundColor: "#F4F7FA" },
};

const COLUMN_LABELS = {
  name: "Lead Name",
  phone: "Phone Number",
  lastFollowUp: "Last Follow Up",
  nextFollowUp: "Next Follow Up",
  leadScore: "Lead Score",
  aiScore: "AI Score",
  status: "Status",
  createdDate: "Created Date",
  updatedDate: "Updated Date",
};

const PRIORITY_OPTIONS = ["LOW", "MEDIUM", "HIGH"];

/* ---------- Shared helpers ---------- */

// Country code + phone as plain digits, e.g. "919595444241". Empty if unusable.
const getPhoneDigits = (lead) => {
  if (!lead?.phone) return "";
  const cc = String(lead.countryCode || "").replace(/\D/g, "");
  const phone = String(lead.phone).replace(/\D/g, "");
  return phone ? `${cc}${phone}` : "";
};

// What we show for the number. Roles without contact access see the raw
// (backend-masked) value untouched.
const displayPhone = (lead, canContact) => {
  if (!canContact) return lead?.phone || "—";
  const digits = getPhoneDigits(lead);
  return digits ? `+${digits}` : lead?.phone || "—";
};

function formatDate(date) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const formatDateTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const formattedDate = date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const formattedTime = date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${formattedDate} ${formattedTime}`;
};

const formatINR = (n) =>
  n === null || n === undefined || n === "" ? "" : `₹${Number(n).toLocaleString("en-IN")}`;

/* ---------- Call + WhatsApp buttons (used in grid, mobile card, drawer) ---------- */
const ContactButtons = ({ lead, onWhatsApp, size = 32 }) => {
  const digits = getPhoneDigits(lead);
  if (!digits) return null;
  return (
    <>
      <Tooltip title="Call">
        <IconButton
          component="a"
          href={`tel:+${digits}`}
          size="small"
          aria-label="Call lead"
          sx={{ ...callBtnSx, width: size, height: size }}
        >
          <CallIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="WhatsApp">
        <IconButton
          size="small"
          aria-label="WhatsApp lead"
          onClick={(e) => onWhatsApp(e, lead)}
          sx={{ ...waBtnSx, height: size }}
        >
          <WhatsAppIcon sx={{ width: 18, height: 18 }} />
          <ArrowDropDownIcon sx={{ fontSize: 18, ml: -0.25 }} />
        </IconButton>
      </Tooltip>
    </>
  );
};

const LeadTable = ({ projectId }) => {
  const [leads, setLeads] = useState([]);
  const [columns, setColumns] = useState([]);
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const isMobile = useMediaQuery("(max-width: 750px)");
  const [openModal, setOpenModal] = useState(false);
  const [openComment, setOpenComment] = useState(false);
  const [comment, setComment] = useState();
  const [selectedLead, setSelectedLead] = useState(null);
  const [status, setStatus] = useState();
  const decoded = jwtDecode(storedUser.accessToken);
  const { role } = useOrganization();
  const canContact = !NO_CONTACT_ROLES.includes(role);
  const [activeFilter, setActiveFilter] = useState("ALL");
  const reminderTimeouts = useRef([]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [subStatusFilter, setSubStatusFilter] = useState(null);
  const [formData, setFormData] = useState({
    nextFollowUp: "",
    status: "",
    remark: "",
  });
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState([]);
  // Single shared WhatsApp menu (was one Menu per row, all bound to the same anchor)
  const [waAnchor, setWaAnchor] = useState(null);
  const [waLead, setWaLead] = useState(null);
  const [assignedToFilter, setAssignedToFilter] = useState([]);
  const [leadScoreFilter, setLeadScoreFilter] = useState([]);
  const [aiScoreRange, setAiScoreRange] = useState({ from: "", to: "" });
  const [createdDateRange, setCreatedDateRange] = useState({ from: "", to: "" });
  const [updatedDateRange, setUpdatedDateRange] = useState({ from: "", to: "" });
  const [nextFollowUpRange, setNextFollowUpRange] = useState({ from: "", to: "" });
  const [lastFollowUpRange, setLastFollowUpRange] = useState({ from: "", to: "" });
  const [leadSourceFilter, setLeadSourceFilter] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [mobilePage, setMobilePage] = useState(0);
  const MOBILE_PAGE_SIZE = 10;
  const [utmFilter, setUtmFilter] = useState({
    utmSource: [],
    utmMedium: [],
    utmCampaign: [],
    utmContent: [],
    utmTerm: [],
  });
  const [utmSources, setUtmSources] = useState([]);
  const [utmMediums, setUtmMediums] = useState([]);
  const [utmCampaigns, setUtmCampaigns] = useState([]);
  const [utmContents, setUtmContents] = useState([]);
  const [utmTerms, setUtmTerms] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [leadScore, setLeadScore] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferLead, setTransferLead] = useState(null);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [updateTargetIds, setUpdateTargetIds] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  // Quick view drawer — store the id so the drawer always shows fresh row data
  const [quickViewId, setQuickViewId] = useState(null);

  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [msgType, setMsgType] = useState("success");
  const showModal = (msg, type = "success") => {
    setMsgText(msg);
    setMsgType(type);
    setMsgOpen(true);
  };
  const isBulkMode = selectedRowIds.length > 1;
  const isBulkUpdate = updateTargetIds.length > 1;
  const orgId = JSON.parse(localStorage.getItem("org"));

  const toggleRowSelection = (leadId) => {
    setSelectedRowIds((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId],
    );
  };

  /* ---------- WhatsApp ---------- */
  const openWhatsAppMenu = (e, lead) => {
    setWaAnchor(e.currentTarget);
    setWaLead(lead);
  };
  const closeWhatsAppMenu = () => setWaAnchor(null);

  const sendWhatsApp = (text) => {
    const digits = getPhoneDigits(waLead);
    if (digits) {
      const url = `https://wa.me/${digits}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
    closeWhatsAppMenu();
  };

  const handleSelectTemplate = (tpl) => {
    const parameters = {
      name: waLead?.name || "there",
      projectName: waLead?.projectName || "",
      senderName: JSON.parse(localStorage.getItem("name")) || "",
    };
    const personalizedMessage = (tpl.template || "").replace(
      /{{\s*(\w+)\s*}}/g,
      (match, key) => parameters[key] ?? match,
    );
    sendWhatsApp(personalizedMessage);
  };

  /* ---------- Reminder ---------- */
  const openReminder = (lead) => {
    setSelectedLead(lead);
    setComment("");
    setOpenComment(true);
  };

  const openFullLead = (leadId) => {
    window.open(`/dashboard/lead/${leadId}`, "_blank", "noopener,noreferrer");
  };

  const fetchTemplate = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/template?projectId=${projectId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setTemplate(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchTemplate();
  }, [projectId]);

  const uniqueAssignees = useMemo(() => {
    const map = {};
    leads.forEach((lead) => {
      (lead.assignedTo || []).forEach((user) => {
        if (user?.name) map[user.name] = user;
      });
    });
    return Object.values(map);
  }, [leads]);

  const uniqueLeadSources = useMemo(() => {
    return [
      ...new Set(
        leads
          .map((lead) => lead.leadSource)
          .filter((val) => val !== null && val !== undefined && val !== ""),
      ),
    ];
  }, [leads]);

  useEffect(() => {
    reminderTimeouts.current.forEach(clearTimeout);
    reminderTimeouts.current = [];

    leads.forEach((lead) => {
      const timeoutId = scheduleFollowUpReminder(lead);
      if (timeoutId) {
        reminderTimeouts.current.push(timeoutId);
      }
    });

    return () => {
      reminderTimeouts.current.forEach(clearTimeout);
    };
  }, [leads]);

  const handleOpenModal = (lead) => {
    setSelectedLead(lead);
    setUpdateTargetIds([lead.leadId]);
    setFormData({
      nextFollowUp: lead.nextFollowUp || "",
      status: lead.status || "",
      remark: "",
    });
    setOpenModal(true);
    setLeadScore(lead.leadScore || []);
  };

  const handleOpenBulkModal = () => {
    if (selectedRowIds.length === 0) return;
    setSelectedLead(null);
    setUpdateTargetIds(selectedRowIds);
    setFormData({
      nextFollowUp: "",
      status: "",
      remark: "",
    });
    setLeadScore("");
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setUpdateTargetIds([]);
  };

  const handleUpdateLead = async () => {
    setBtnLoading(true);
    try {
      if (isBulkUpdate) {
        if (!(formData.nextFollowUp && formData.status)) {
          showModal(
            "Please select a next follow up date and status for the selected leads",
            "error",
          );
          setBtnLoading(false);
          return;
        }

        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/lead/update-lead-status/bulk`,
          {
            username: storedUser.username,
            leadIds: updateTargetIds,
            lastFollowUp: new Date().toISOString().split("T")[0],
            nextFollowUp: formData.nextFollowUp,
            status: formData.status,
          },
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        );

        await fetchLeads();
        showModal(`${updateTargetIds.length} leads updated successfully`, "success");

        setSelectedRowIds([]);
        setUpdateTargetIds([]);
        setOpenModal(false);
        setFormData({
          nextFollowUp: "",
          status: "",
          remark: "",
        });
        return;
      }

      const hasRemark = formData.remark && formData.remark.trim() !== "";
      const hasUpdate = formData.nextFollowUp || formData.status || hasRemark;

      if (hasUpdate) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/lead/update-lead-status`,
          {
            username: storedUser.username,
            leadId: selectedLead.leadId,
            lastFollowUp: new Date().toISOString().split("T")[0],
            nextFollowUp: formData.nextFollowUp || selectedLead.nextFollowUp,
            remark: formData.remark,
            status: formData.status || selectedLead.status,
          },
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        fetchLeads();
      } else {
        return;
      }

      showModal("Lead updated successfully", "success");

      setLeads((prev) =>
        prev.map((l) =>
          l.leadId === selectedLead.leadId ? { ...l, ...formData } : l,
        ),
      );

      setOpenModal(false);
      setUpdateTargetIds([]);
      setFormData({
        nextFollowUp: "",
        status: "",
        remark: "",
      });
    } catch (err) {
      console.log(err);
      showModal("Failed to update lead", "error");
    } finally {
      setBtnLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!comment || comment.trim() === "") {
      showModal("Please select a date and time for the reminder.", "error");
      return;
    }
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/lead?leadId=${selectedLead.leadId}`,
        { reminder: comment },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setOpenComment(false);
      setComment("");
      showModal("Reminder set", "success");
      fetchLeads();
    } catch (error) {
      console.log(error);
    }
  };

  const handleWriteWithAI = async () => {
    try {
      setAiLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/lead/write-comment`,
        {
          leadName: selectedLead?.name,
          requirement: selectedLead?.requirement,
          preferredPropertyType: selectedLead?.preferredPropertyType,
          status: formData.status,
          existingText: formData.remark || "",
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setFormData((prev) => ({ ...prev, remark: res.data }));
    } catch (error) {
      console.log(error);
      showModal(error?.response?.data?.Exception || "Failed to generate comment", "error");
    } finally {
      setAiLoading(false);
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    setLeads([]);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/lead/all?projectId=${projectId}&orgId=${orgId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );

      const rows = Array.isArray(res.data) ? res.data : [];
      const data = rows.map((lead) => ({
        ...lead,
        remarksText: (lead.followUpHistories || [])
          .map((h) => h.remark || "")
          .join(" "),
      }));
      setLeads(data);

      if (data.length > 0) {
        const nonNullColumns = Object.keys(data[0]).filter((key) =>
          data.some(
            (row) =>
              row[key] !== null &&
              row[key] !== "" &&
              key !== "assignedBy" &&
              key !== "reminder" &&
              key !== "utmSource" &&
              key !== "utmMedium" &&
              key !== "utmCampaign" &&
              key !== "utmContent" &&
              key !== "utmTerm" &&
              key !== "remarksText" &&
              key !== "priority" &&
              key !== "leadSource",
          ),
        );
        setColumns(nonNullColumns);

        const getUniqueValues = (arr, key) => {
          return [
            ...new Set(
              arr
                ?.map((item) => item[key])
                .filter(
                  (val) => val !== null && val !== undefined && val !== "",
                ),
            ),
          ];
        };

        setUtmSources(getUniqueValues(data, "utmSource"));
        setUtmMediums(getUniqueValues(data, "utmMedium"));
        setUtmCampaigns(getUniqueValues(data, "utmCampaign"));
        setUtmContents(getUniqueValues(data, "utmContent"));
        setUtmTerms(getUniqueValues(data, "utmTerm"));
      }
    } catch (err) {
      setLeads([]);
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!storedUser?.accessToken) return;
    if (!projectId) return;
    fetchLeads();
  }, [projectId]);

  useEffect(() => {
    const fetchLeadStatus = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/lead-status/all?projectId=${projectId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setStatus(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchLeadStatus();
  }, [projectId]);

  // status -> meta group, used by filters and the quick view pill
  const statusToMeta = useMemo(
    () =>
      (status || []).reduce((acc, item) => {
        acc[item.status] = item.metaLeadStatus?.status || "Unknown";
        return acc;
      }, {}),
    [status],
  );

  const gridColumns = columns
    .filter(
      (key) =>
        key !== "isLead" &&
        key !== "leadId" &&
        key !== "email" &&
        key !== "utmSource" &&
        key !== "utmMedium" &&
        key !== "utmCampaign" &&
        key !== "utmContent" &&
        key !== "utmTerm" &&
        key !== "countryCode" &&
        key !== "followUpHistories",
    )
    .map((key) => {
      if (key === "name") {
        return {
          field: key,
          headerName: "Lead Name",
          minWidth: 180,
          renderCell: (params) => (
            <Box
              sx={{
                cursor: "pointer",
                fontWeight: 700,
                color: INK,
                "&:hover": { color: PRIMARY, textDecoration: "underline", textUnderlineOffset: "3px" },
              }}
              onClick={() => setQuickViewId(params.row.leadId)}
            >
              {params.row.name} {params.row.isLead ? "⭐" : null}
            </Box>
          ),
        };
      }
      if (key === "phone") {
        return {
          field: key,
          headerName: COLUMN_LABELS[key] || key.replace(/([A-Z])/g, " $1"),
          minWidth: canContact ? 230 : 180,
          renderCell: (params) => (
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  fontFamily: MONO,
                  fontSize: 12.5,
                  color: INK_2,
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayPhone(params.row, canContact)}
              </span>
              {canContact && (
                <ContactButtons lead={params.row} onWhatsApp={openWhatsAppMenu} />
              )}
            </div>
          ),
        };
      }
      if (key === "assignedTo") {
        return {
          field: key,
          headerName: "Assigned To",
          minWidth: 180,
          renderCell: (params) => (
            <Box sx={{ color: INK_2, overflow: "hidden", textOverflow: "ellipsis" }}>
              {params.row.assignedTo.length > 0
                ? params.row.assignedTo.map((user) => user.name).join(", ")
                : "ADMIN"}
            </Box>
          ),
        };
      }
      if (key === "aiScore") {
        return {
          field: key,
          headerName: "AI Score",
          minWidth: 130,
          renderCell: (params) => <AiScoreChip score={params.row.aiScore} />,
        };
      }
      if (key === "createdDate" || key === "updatedDate") {
        return {
          field: key,
          headerName: COLUMN_LABELS[key],
          minWidth: 180,
          renderCell: (params) => <Box sx={{ fontFamily: MONO, fontSize: 12.5, color: INK_2 }}>{formatDateTime(params.row[key])}</Box>,
        };
      }

      if (key === "nextFollowUp" || key === "lastFollowUp") {
        return {
          field: key,
          headerName: COLUMN_LABELS[key],
          minWidth: 180,
          renderCell: (params) => <Box sx={{ fontFamily: MONO, fontSize: 12.5, color: INK_2 }}>{formatDate(params.row[key])}</Box>,
        };
      }

      return {
        field: key,
        headerName: COLUMN_LABELS[key] || key.replace(/([A-Z])/g, " $1"),
        minWidth: 180,
      };
    });

  gridColumns.unshift({
    field: "select",
    headerName: "",
    width: 70,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <input
        type="checkbox"
        checked={selectedRowIds.includes(params.row.leadId)}
        onChange={() => toggleRowSelection(params.row.leadId)}
        style={{
          width: 17,
          height: 17,
          cursor: "pointer",
          margin: "0",
          accentColor: PRIMARY,
        }}
      />
    ),
  });

  gridColumns.push({
    field: "actions",
    headerName: "Actions",
    width: 260,
    sortable: false,
    renderCell: (params) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, height: "100%" }}>
        <Button
          variant="contained"
          size="small"
          sx={{ mx: 0 }}
          disabled={isBulkMode}
          onClick={() => handleOpenModal(params.row)}
        >
          Update
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={{ mx: 0 }}
          disabled={isBulkMode}
          onClick={() => {
            setTransferLead(params.row);
            setTransferOpen(true);
          }}
        >
          Transfer
        </Button>
        <Tooltip title="Set reminder">
          <span>
            <IconButton
              size="small"
              aria-label="Set reminder"
              sx={bellBtnSx}
              disabled={isBulkMode}
              onClick={() => openReminder(params.row)}
            >
              <NotificationsNoneIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    ),
  });

  const isToday = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const date = new Date(dateStr);
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isInCurrentWeek = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const date = new Date(dateStr);
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    return date > today && date >= startOfWeek && date <= endOfWeek;
  };

  const isDateInRange = (dateStr, range) => {
    if (!range?.from && !range?.to) return true;
    if (!dateStr) return false;
    const date = new Date(dateStr);
    date.setHours(0, 0, 0, 0);
    if (range.from) {
      const from = new Date(range.from);
      from.setHours(0, 0, 0, 0);
      if (date < from) return false;
    }
    if (range.to) {
      const to = new Date(range.to);
      to.setHours(0, 0, 0, 0);
      if (date > to) return false;
    }
    return true;
  };

  const filteredLeads = (() => {
    let base = leads;

    if (assignedToFilter.length > 0) {
      base = base.filter((lead) => {
        const names = (lead.assignedTo || []).map((u) => u.name);
        // "None" = leads with nobody assigned
        if (names.length === 0) return assignedToFilter.includes(NONE_OPTION);
        return names.some((n) => assignedToFilter.includes(n));
      });
    }

    if (leadScoreFilter.length > 0) {
      base = base.filter((lead) => noneMatch(leadScoreFilter, lead.leadScore));
    }

    if (
      utmFilter?.utmSource?.length ||
      utmFilter?.utmMedium?.length ||
      utmFilter?.utmCampaign?.length ||
      utmFilter?.utmContent?.length ||
      utmFilter?.utmTerm?.length
    ) {
      base = base.filter((lead) => {
        return (
          noneMatch(utmFilter.utmSource, lead.utmSource) &&
          noneMatch(utmFilter.utmMedium, lead.utmMedium) &&
          noneMatch(utmFilter.utmCampaign, lead.utmCampaign) &&
          noneMatch(utmFilter.utmContent, lead.utmContent) &&
          noneMatch(utmFilter.utmTerm, lead.utmTerm)
        );
      });
    }

    if (createdDateRange.none) {
      // "None" = leads with no date set
      base = base.filter((lead) => isBlank(lead.createdDate));
    } else if (createdDateRange.from || createdDateRange.to) {
      base = base.filter((lead) =>
        isDateInRange(lead.createdDate, createdDateRange),
      );
    }
    if (updatedDateRange.none) {
      // "None" = leads with no date set
      base = base.filter((lead) => isBlank(lead.updatedDate));
    } else if (updatedDateRange.from || updatedDateRange.to) {
      base = base.filter((lead) =>
        isDateInRange(lead.updatedDate, updatedDateRange),
      );
    }
    if (nextFollowUpRange.none) {
      // "None" = leads with no date set
      base = base.filter((lead) => isBlank(lead.nextFollowUp));
    } else if (nextFollowUpRange.from || nextFollowUpRange.to) {
      base = base.filter((lead) =>
        isDateInRange(lead.nextFollowUp, nextFollowUpRange),
      );
    }
    if (lastFollowUpRange.none) {
      // "None" = leads with no date set
      base = base.filter((lead) => isBlank(lead.lastFollowUp));
    } else if (lastFollowUpRange.from || lastFollowUpRange.to) {
      base = base.filter((lead) =>
        isDateInRange(lead.lastFollowUp, lastFollowUpRange),
      );
    }
    if (leadSourceFilter.length > 0) {
      base = base.filter((lead) => noneMatch(leadSourceFilter, lead.leadSource));
    }
    if (priorityFilter.length > 0) {
      base = base.filter((lead) => noneMatch(priorityFilter, lead.priority));
    }

    if (aiScoreRange.none) {
      // "None" = leads with no AI score
      base = base.filter((lead) => isBlank(lead.aiScore));
    } else if (aiScoreRange.from !== "" || aiScoreRange.to !== "") {
      base = base.filter((lead) => {
        if (lead.aiScore === null || lead.aiScore === undefined) return false;
        const score = Number(lead.aiScore);
        if (aiScoreRange.from !== "" && score < Number(aiScoreRange.from))
          return false;
        if (aiScoreRange.to !== "" && score > Number(aiScoreRange.to))
          return false;
        return true;
      });
    }

    return base;
  })();

  const todaysTasks = filteredLeads.filter(
    (lead) => lead.nextFollowUp && isToday(lead.nextFollowUp),
  );

  const upcomingTasks = filteredLeads.filter(
    (lead) => lead.nextFollowUp && isInCurrentWeek(lead.nextFollowUp),
  );

  const missedTasks = filteredLeads.filter((lead) => {
    if (!lead.nextFollowUp) return false;
    const nextFollowUpDate = new Date(lead.nextFollowUp);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    nextFollowUpDate.setHours(0, 0, 0, 0);
    if (nextFollowUpDate >= today) return false;
    return true;
  });

  const finalLeads = useMemo(() => {
    let base = filteredLeads;
    switch (activeFilter) {
      case "TODAY":
        base = todaysTasks;
        break;
      case "UPCOMING":
        base = upcomingTasks;
        break;
      case "MISSED":
        base = missedTasks;
        break;
      default:
        base = filteredLeads;
    }

    if (statusFilter) {
      base = base.filter((lead) => {
        const leadStatus = lead.status || "NEW";
        const meta = statusToMeta[leadStatus] || "Unknown";
        return meta === statusFilter;
      });
    }

    if (subStatusFilter) {
      base = base.filter((lead) => (lead.status || "NEW") === subStatusFilter);
    }
    return base;
  }, [
    activeFilter,
    todaysTasks,
    upcomingTasks,
    missedTasks,
    filteredLeads,
    statusFilter,
    subStatusFilter,
    statusToMeta,
  ]);

  const cardLeads = useMemo(() => {
    switch (activeFilter) {
      case "TODAY":
        return todaysTasks;
      case "UPCOMING":
        return upcomingTasks;
      case "MISSED":
        return missedTasks;
      default:
        return filteredLeads;
    }
  }, [activeFilter, todaysTasks, upcomingTasks, missedTasks, filteredLeads]);

  if (projectId === null || projectId === "")
    return (
      <p
        style={{
          margin: "8px 0",
          padding: "28px 16px",
          textAlign: "center",
          background: "#FFFFFF",
          border: `1px solid ${LINE}`,
          borderRadius: 14,
          fontFamily: FONT,
          fontSize: 14,
          fontWeight: 500,
          color: MUTED,
        }}
      >
        Please select a project
      </p>
    );
  if (loading) {
    return (
      <ThemeProvider theme={tableTheme}>
        <Box>
          <Skeleton variant="rounded" height={72} sx={{ mt: 2, borderRadius: "14px" }} />
          <Skeleton variant="rounded" height={72} sx={{ mt: 2, borderRadius: "14px" }} />
          <Skeleton variant="rounded" height={300} sx={{ mt: 2, borderRadius: "14px" }} />
        </Box>
      </ThemeProvider>
    );
  }

  const searchedLeads = finalLeads.filter((lead) => {
    const value = searchText.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(value) ||
      lead.phone?.toLowerCase().includes(value) ||
      lead.email?.toLowerCase().includes(value) ||
      lead.status?.toLowerCase().includes(value) ||
      lead.remarksText?.toLowerCase().includes(value)
    );
  });

  const totalMobilePages = Math.ceil(searchedLeads.length / MOBILE_PAGE_SIZE);
  const paginatedMobileLeads = searchedLeads.slice(
    mobilePage * MOBILE_PAGE_SIZE,
    mobilePage * MOBILE_PAGE_SIZE + MOBILE_PAGE_SIZE,
  );

  const sortedStatus = [...(status || [])].sort((a, b) => {
    const groupA = a.metaLeadStatus?.status || "";
    const groupB = b.metaLeadStatus?.status || "";
    if (groupA !== groupB) {
      return groupA.localeCompare(groupB);
    }
    return a.status.localeCompare(b.status);
  });

  const leadScoreOptions = [
    { label: "Excellent", options: [100, 90, 80] },
    { label: "Good", options: [70, 60, 50] },
    { label: "Warm", options: [50, 40, 30] },
    { label: "Cold", options: [20, 10, 0] },
  ].flatMap((group) =>
    group.options.map((score) => ({
      group: group.label,
      value: score,
    })),
  );

  const quickViewLead = quickViewId
    ? leads.find((l) => l.leadId === quickViewId) || null
    : null;

  // Task-tab tones (visual only)
  const TAB_TONE = {
    TODAY: { bg: "#FDF4E1", edge: "#E8A33D", ink: "#8A5A08" },
    UPCOMING: { bg: "#EAF2FB", edge: "#1F6FEB", ink: "#1B4F82" },
    MISSED: { bg: "#FBEDEB", edge: "#D9493D", ink: "#A6342A" },
    ALL: { bg: "#EFEDFB", edge: "#6C5CE7", ink: "#4A3FBF" },
  };
  const tabStyle = (key) => {
    const on = activeFilter === key;
    const t = TAB_TONE[key];
    return {
      padding: "12px 16px",
      borderRadius: 14,
      cursor: "pointer",
      background: on ? t.bg : "#FFFFFF",
      border: `1px solid ${on ? t.edge : LINE}`,
      boxShadow: on ? `inset 0 0 0 1px ${t.edge}` : "none",
      color: on ? t.ink : INK_2,
      fontFamily: FONT,
      fontSize: 13.5,
      fontWeight: 700,
      minWidth: 0,
      textAlign: "left",
      transition: "background .15s, border-color .15s",
    };
  };
  const tabCountStyle = (key) => ({
    fontFamily: MONO,
    fontSize: 22,
    fontWeight: 600,
    letterSpacing: "-0.03em",
    marginTop: 2,
    color: activeFilter === key ? TAB_TONE[key].ink : INK,
  });

  // datetime-local adapter for the reminder modal: comment stays "YYYY-MM-DDTHH:mm"
  const reminderDate = (comment || "").slice(0, 10);
  const reminderTime = (comment || "").slice(11, 16);
  const setReminderPart = (d, t) => {
    if (!d && !t) return setComment("");
    setComment(`${d || new Date().toISOString().slice(0, 10)}T${t || "09:00"}`);
  };

  return (
    <ThemeProvider theme={tableTheme}>
    <div style={{ fontFamily: FONT, color: INK }}>
      <Box
        sx={{
          display: "grid",
          gap: 1.25,
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(4, 1fr)",
          },
        }}
      >
        <div onClick={() => setActiveFilter("TODAY")} style={tabStyle("TODAY")}>
          Today's Tasks
          <div style={tabCountStyle("TODAY")}>
            {todaysTasks.length} <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: MUTED, letterSpacing: 0 }}>Leads</span>
          </div>
        </div>

        <div onClick={() => setActiveFilter("UPCOMING")} style={tabStyle("UPCOMING")}>
          Upcoming Tasks
          <div style={tabCountStyle("UPCOMING")}>
            {upcomingTasks.length} <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: MUTED, letterSpacing: 0 }}>Leads</span>
          </div>
        </div>

        <div onClick={() => setActiveFilter("MISSED")} style={tabStyle("MISSED")}>
          Missed Tasks
          <div style={tabCountStyle("MISSED")}>
            {missedTasks.length} <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: MUTED, letterSpacing: 0 }}>Leads</span>
          </div>
        </div>

        <div onClick={() => setActiveFilter("ALL")} style={tabStyle("ALL")}>
          All Leads
          <div style={tabCountStyle("ALL")}>
            {filteredLeads.length} <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: MUTED, letterSpacing: 0 }}>Leads</span>
          </div>
        </div>
      </Box>

      <LeadCountCards
        leads={cardLeads}
        metaStatuses={status}
        onFilterChange={(meta) => {
          setStatusFilter(meta);
          setSubStatusFilter(null);
        }}
        activeStatusFilter={statusFilter}
        activeSubStatusFilter={subStatusFilter}
        onSubStatusFilterChange={setSubStatusFilter}
        allLeads={leads}
      />

      <Box sx={{ my: 2 }}>
        <Button
          variant="outlined"
          onClick={() => setShowAdvancedFilters((prev) => !prev)}
          endIcon={
            showAdvancedFilters ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )
          }
          sx={{ height: 36 }}
        >
          {showAdvancedFilters
            ? "Hide Advanced Filters"
            : "Show Advanced Filters"}
        </Button>
      </Box>

      <Collapse in={showAdvancedFilters}>
        <Box sx={{ background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: "14px", p: { xs: 1.75, sm: 2.25 }, mb: 2 }}>
        <Grid container spacing={2} mb={2}>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <MultiSelect
              label="UTM Source"
              includeNone
              options={utmSources}
              value={utmFilter.utmSource}
              onChange={(val) => setUtmFilter({ ...utmFilter, utmSource: val })}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <MultiSelect
              label="UTM Campaign"
              includeNone
              options={utmCampaigns}
              value={utmFilter.utmCampaign}
              onChange={(val) =>
                setUtmFilter({ ...utmFilter, utmCampaign: val })
              }
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <MultiSelect
              label="UTM Medium"
              includeNone
              options={utmMediums}
              value={utmFilter.utmMedium}
              onChange={(val) => setUtmFilter({ ...utmFilter, utmMedium: val })}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <MultiSelect
              label="UTM Content"
              includeNone
              options={utmContents}
              value={utmFilter.utmContent}
              onChange={(val) =>
                setUtmFilter({ ...utmFilter, utmContent: val })
              }
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <MultiSelect
              label="UTM Term"
              includeNone
              options={utmTerms}
              value={utmFilter.utmTerm}
              onChange={(val) => setUtmFilter({ ...utmFilter, utmTerm: val })}
            />
          </Grid>
          {(role === "ADMIN" || role === "PROJECT_HEAD") && (
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <MultiSelect
                label="Assigned To"
              includeNone
                options={uniqueAssignees?.map((u) => u.name)}
                value={assignedToFilter}
                onChange={(val) => setAssignedToFilter(val)}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Autocomplete
              multiple
              options={[{ group: "", value: NONE_OPTION }, ...leadScoreOptions]}
              groupBy={(option) => option.group}
              getOptionLabel={(option) =>
                option.value === NONE_OPTION ? "None" : option.value.toString()
              }
              value={[{ group: "", value: NONE_OPTION }, ...leadScoreOptions].filter((opt) =>
                leadScoreFilter.includes(opt.value),
              )}
              onChange={(event, newValue) => {
                setLeadScoreFilter(newValue.map((item) => item.value));
              }}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Lead Score" />
              )}
              size="small"
              sx={{
                "& .MuiInputBase-root": {
                  padding: "4px 6px",
                },
                "& input::placeholder": {
                  color: "#8A97A8 !important",
                  opacity: 1,
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <MultiSelect
              label="Lead Source"
              includeNone
              options={uniqueLeadSources}
              value={leadSourceFilter}
              onChange={setLeadSourceFilter}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <MultiSelect
              label="Priority"
              includeNone
              options={PRIORITY_OPTIONS}
              value={priorityFilter}
              onChange={setPriorityFilter}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <NumberRangeFilter
              label="AI Score"
              value={aiScoreRange}
              onChange={setAiScoreRange}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DateRangeFilter
              label="Created Date"
              value={createdDateRange}
              onChange={setCreatedDateRange}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DateRangeFilter
              label="Updated Date"
              value={updatedDateRange}
              onChange={setUpdatedDateRange}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DateRangeFilter
              label="Next Follow Up"
              value={nextFollowUpRange}
              onChange={setNextFollowUpRange}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DateRangeFilter
              label="Last Follow Up"
              value={lastFollowUpRange}
              onChange={setLastFollowUpRange}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", gap: 1, pt: 1.5, borderTop: "1px solid #EDF1F5" }}>
        <Button
          className="btn-contained"
          variant="contained"
          sx={{ mr: 0, height: 38, px: 2.5, margin: "0 !important" }}
          onClick={() => setShowAdvancedFilters((prev) => !prev)}
        >
          Apply
        </Button>
        <Button
          className="btn-contained"
          variant="outlined"
          sx={{ height: 38, px: 2.5, margin: "0 !important", background: "#FFFFFF !important", color: `${INK} !important`, border: `1px solid ${FIELD_EDGE} !important` }}
          onClick={() => {
            setAssignedToFilter([]);
            setLeadScoreFilter([]);
            setCreatedDateRange({ from: "", to: "" });
            setUpdatedDateRange({ from: "", to: "" });
            setNextFollowUpRange({ from: "", to: "" });
            setLastFollowUpRange({ from: "", to: "" });
            setLeadSourceFilter([]);
            setPriorityFilter([]);
            setAiScoreRange({ from: "", to: "" });
          }}
        >
          Clear
        </Button>
        </Box>
        </Box>
      </Collapse>

      <Box
        sx={{
          display: { xs: "block", sm: "flex" },
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography
          variant="h4"
          my={2}
          fontWeight={700}
          sx={{ fontFamily: FONT, fontSize: { xs: 24, sm: 27 }, fontWeight: 800, letterSpacing: "-0.028em", color: INK }}
        >
          My Leads{" "}
          <span style={{ fontSize: "13.5px", fontWeight: 500, letterSpacing: 0, color: "#4A5A6E" }}>
            (Showing {activeFilter} leads of{" "}
            {subStatusFilter
              ? subStatusFilter
              : statusFilter
                ? statusFilter
                : "All"}{" "}
            status)
          </span>
        </Typography>
        {role !== "VT1" && role !== "VT2" && (
          <Button
            className="btn-contained"
            variant="contained"
            onClick={() => navigate(`/dashboard/add-lead/${projectId}`)}
            sx={{
              margin: "0 !important",
              width: { xs: "100%", sm: "auto" },
              height: { xs: 44, sm: 38 },
              px: 2,
              gap: 1,
              whiteSpace: "nowrap",
              background: `${PRIMARY} !important`,
              color: "#FFFFFF !important",
              border: "0 !important",
              borderRadius: "10px !important",
              "&:hover": { background: "#084767 !important" },
            }}
          >
            <PersonAddAltRoundedIcon sx={{ fontSize: 18 }} /> Add Lead
          </Button>
        )}
      </Box>

      {isMobile && (
        <input
          type="text"
          placeholder="Search leads..."
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setMobilePage(0);
          }}
          style={{
            width: "100%",
            height: 44,
            padding: "0 14px",
            marginTop: "14px",
            marginBottom: "0",
            borderRadius: "12px",
            border: `1px solid ${FIELD_EDGE}`,
            background: "#FFFFFF",
            boxSizing: "border-box",
            fontFamily: FONT,
            fontSize: 14,
            color: INK,
            outline: "none",
          }}
        />
      )}

      {isMobile && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
          {paginatedMobileLeads.map((lead) => (
            <div
              key={lead.leadId}
              className="card"
              style={{
                margin: 0,
                padding: "14px 14px 10px",
                background: "#FFFFFF",
                border: `1px solid ${LINE}`,
                borderRadius: 14,
                boxShadow: "none",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <Typography
                    fontSize={"16px"}
                    fontWeight={"800"}
                    onClick={() => setQuickViewId(lead.leadId)}
                    sx={{
                      fontFamily: FONT,
                      letterSpacing: "-0.015em",
                      color: INK,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                    }}
                  >
                    {lead.name} {lead.isLead ? "⭐" : null}
                  </Typography>
                  <Typography fontSize={"12px"} color={MUTED} fontWeight={"500"} sx={{ fontFamily: FONT }}>
                    Lead Score: <span style={{ fontFamily: MONO, fontWeight: 600, color: INK_2 }}>{lead.leadScore}</span>
                  </Typography>
                  {lead.aiScore !== null && lead.aiScore !== undefined && (
                    <Typography fontSize={"12px"} color={MUTED} fontWeight={"500"} sx={{ fontFamily: FONT }}>
                      AI Score: <span style={{ fontFamily: MONO, fontWeight: 600, color: INK_2 }}>{lead.aiScore}</span>
                    </Typography>
                  )}
                </div>
                <div style={{ flexShrink: 0, textAlign: "right" }}>
                  <Typography fontSize={"10.5px"} color={MUTED} fontWeight={"700"} sx={{ fontFamily: FONT, letterSpacing: "0.09em" }}>
                    NEXT FOLLOW UP
                  </Typography>
                  <Typography fontSize={"13px"} color={INK} fontWeight={"600"} sx={{ fontFamily: MONO, mt: 0.25 }}>
                    {formatDate(lead.nextFollowUp)}
                  </Typography>
                </div>
              </div>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontFamily: MONO,
                    fontSize: 13,
                    fontWeight: 500,
                    color: INK_2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayPhone(lead, canContact)}
                </span>
                {canContact && (
                  <ContactButtons lead={lead} onWhatsApp={openWhatsAppMenu} size={36} />
                )}
              </Box>

              {lead.email && (
                <Typography
                  color={INK_2}
                  fontSize={"13px"}
                  fontWeight={500}
                  mt={0.75}
                  sx={{ display: "flex", alignItems: "center", gap: 1, fontFamily: FONT, wordBreak: "break-all" }}
                >
                  <EmailIcon sx={{ fontSize: 16, color: MUTED }} /> {lead.email}
                </Typography>
              )}

              <hr style={{ border: 0, borderTop: "1px solid #F1F4F8", margin: "12px -14px 10px" }} />
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  sx={{ flex: 1, height: 40 }}
                  onClick={() => setQuickViewId(lead.leadId)}
                >
                  View
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  sx={{ ml: 0, flex: 1, height: 40 }}
                  onClick={() => {
                    setTransferLead(lead);
                    setTransferOpen(true);
                  }}
                >
                  Transfer
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ mx: 0, flex: 1, height: 40 }}
                  onClick={() => handleOpenModal(lead)}
                >
                  Remark
                </Button>
                <IconButton
                  aria-label="Set reminder"
                  onClick={() => openReminder(lead)}
                  sx={{ ...bellBtnSx, width: 40, height: 40, flexShrink: 0 }}
                >
                  <NotificationsNoneIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </div>
            </div>
          ))}
          {totalMobilePages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                mt: 1,
                mb: 3,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                sx={{ height: 38, px: 2 }}
                disabled={mobilePage === 0}
                onClick={() => {
                  setMobilePage((p) => p - 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                Previous
              </Button>

              <Typography fontSize={"13px"} color={MUTED} fontWeight={600} sx={{ fontFamily: MONO }}>
                Page {mobilePage + 1} of {totalMobilePages}
              </Typography>

              <Button
                variant="outlined"
                size="small"
                sx={{ height: 38, px: 2 }}
                disabled={mobilePage >= totalMobilePages - 1}
                onClick={() => {
                  setMobilePage((p) => p + 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                Next
              </Button>
            </Box>
          )}
          {searchedLeads.length === 0 && (
            <Typography textAlign="center" color={MUTED} mt={3} fontSize="14px">
              No leads found.
            </Typography>
          )}
        </div>
      )}

      {selectedRowIds.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            justifyContent: "space-between",
            background: "#EAF2FB",
            border: "1px solid #8FC3E6",
            borderRadius: "12px",
            p: "10px 14px",
            my: 2,
          }}
        >
          <Typography fontWeight={700} fontSize={14} sx={{ color: "#1B4F82" }}>
            <span style={{ fontFamily: MONO }}>{selectedRowIds.length}</span> lead{selectedRowIds.length > 1 ? "s" : ""}{" "}
            selected
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" size="small" onClick={handleOpenBulkModal}>
              Update Selected
            </Button>
            <Button variant="outlined" size="small" onClick={() => setSelectedRowIds([])}>
              Clear Selection
            </Button>
          </Box>
        </Box>
      )}
      {!isMobile && (
        <LeadDataTable
          rows={searchedLeads}
          columns={gridColumns}
          loading={loading}
          getRowId={(row) => row.leadId}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
          }
        />
      )}

      {/* ── Shared WhatsApp template menu (one instance for all rows) ── */}
      <Menu
        anchorEl={waAnchor}
        open={Boolean(waAnchor)}
        onClose={closeWhatsAppMenu}
        slotProps={{ paper: { sx: waMenuPaper } }}
      >
        {template?.length === 0 ? (
          <MenuItem disabled sx={{ ...waItemSx, color: MUTED }}>
            No templates added
          </MenuItem>
        ) : (
          template?.map((tpl) => (
            <MenuItem
              key={tpl.templateId}
              onClick={() => handleSelectTemplate(tpl)}
              sx={waItemSx}
            >
              {tpl.title}
            </MenuItem>
          ))
        )}
        <MenuItem
          onClick={() => sendWhatsApp("")}
          sx={{
            ...waItemSx,
            fontWeight: 700,
            color: WA,
            borderTop: template?.length > 0 ? "1px solid #EDF1F5" : "none",
          }}
        >
          Custom Message
        </MenuItem>
      </Menu>

      {/* ── Quick view drawer ── */}
      <LeadQuickView
        open={Boolean(quickViewLead)}
        lead={quickViewLead}
        onClose={() => setQuickViewId(null)}
        isMobile={isMobile}
        canContact={canContact}
        metaGroup={quickViewLead ? statusToMeta[quickViewLead.status || "NEW"] : ""}
        accessToken={storedUser.accessToken}
        onWhatsApp={openWhatsAppMenu}
        onReminder={openReminder}
        onUpdate={handleOpenModal}
        onOpenFull={openFullLead}
      />

      <Modal open={openModal} onClose={handleCloseModal}>
        <Box sx={modalBoxSx(isMobile)}>
          <h3 style={modalTitleStyle}>Update Lead</h3>
          {isBulkUpdate && (
            <Typography
              fontSize={13}
              mb={1.5}
              sx={{ p: "8px 12px", background: "#EAF2FB", border: "1px solid #C9DEF3", borderRadius: "10px", color: "#1B4F82", fontWeight: 600 }}
            >
              Updating <span style={{ fontFamily: MONO }}>{updateTargetIds.length}</span> leads
            </Typography>
          )}

          <label htmlFor="" style={labelStyle}>Next Follow Up Date</label>
          <ThemedDatePicker
            name="nextFollowUp"
            value={formData.nextFollowUp}
            onChange={(e) =>
              setFormData({ ...formData, nextFollowUp: e.target.value })
            }
            sx={{ mb: 2 }}
          />

          <label htmlFor="" style={labelStyle}>Lead Status</label>
          <Autocomplete
            name="status"
            options={sortedStatus || []}
            getOptionLabel={(option) => option.status || ""}
            groupBy={(option) => option.metaLeadStatus.status}
            value={status?.find((p) => p.status === formData.status) || null}
            onChange={(e, value) => {
              setFormData({ ...formData, status: value?.status || "" });
            }}
            isOptionEqualToValue={(option, value) =>
              option.status === value.status
            }
            renderInput={(params) => (
              <TextField {...params} placeholder="Select Status" />
            )}
            sx={{
              margin: "0 0 16px 0",
              "& .MuiInputBase-root": {
                minHeight: 42,
                fontSize: "14px",
                borderRadius: "10px",
                padding: "1px 7px",
              },
            }}
          />

          {!isBulkUpdate && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.75,
                }}
              >
                <label htmlFor="" style={{ ...labelStyle, margin: 0 }}>Remark</label>
                <Button
                  size="small"
                  startIcon={<AutoFixHighIcon fontSize="small" />}
                  onClick={handleWriteWithAI}
                  disabled={aiLoading}
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    minWidth: 0,
                    color: "#4A3FBF",
                    background: "#EFEDFB",
                    border: "1px solid #D3D0F2",
                    "&:hover": { background: "#E7E4F8" },
                  }}
                >
                  {aiLoading ? "Writing..." : "Write with AI"}
                </Button>
              </Box>
              <textarea
                name="remark"
                rows={3}
                value={formData.remark}
                onChange={(e) =>
                  setFormData({ ...formData, remark: e.target.value })
                }
                style={{
                  display: "block",
                  width: "100%",
                  boxSizing: "border-box",
                  minHeight: 88,
                  padding: "10px 12px",
                  margin: "0 0 16px",
                  background: "#FFFFFF",
                  border: `1px solid ${FIELD_EDGE}`,
                  borderRadius: 10,
                  fontFamily: FONT,
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: INK,
                  outline: "none",
                  resize: "vertical",
                }}
              />

              <label htmlFor="" style={labelStyle}>Lead Score</label>
              <Autocomplete
                options={leadScoreOptions}
                groupBy={(option) => option.group}
                getOptionLabel={(option) => option.value.toString()}
                value={
                  leadScoreOptions?.find((opt) => opt.value === leadScore) ||
                  null
                }
                onChange={(event, newValue) => {
                  setLeadScore(newValue?.value || null);
                }}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Lead Score" />
                )}
                sx={{
                  "& .MuiInputBase-root": {
                    padding: "4px 6px",
                  },
                  "& input::placeholder": {
                    color: "#8A97A8 !important",
                    opacity: 1,
                  },
                }}
              />
            </>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
            {!isBulkUpdate &&
              selectedLead?.assignedBy?.email === decoded?.sub && (
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 2, height: 42 }}
                  onClick={() =>
                    navigate(`/dashboard/edit-lead/${selectedLead.leadId}`)
                  }
                >
                  Edit Lead
                </Button>
              )}
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 2, height: 42 }}
              onClick={handleUpdateLead}
              disabled={btnLoading}
            >
              Submit
            </Button>
          </div>
        </Box>
      </Modal>

      <Modal open={openComment} onClose={() => setOpenComment(false)}>
        <Box sx={modalBoxSx(isMobile)}>
          <h3 style={modalTitleStyle}>
            Set Reminder for{" "}
            <span style={{ color: PRIMARY }}>{selectedLead?.name || "lead"}</span>
          </h3>

          <label htmlFor="" style={labelStyle}>Select Date Time</label>
          <Box sx={{ display: "flex", gap: 1 }}>
            <ThemedDatePicker
              name="reminder"
              value={reminderDate}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setReminderPart(e.target.value, reminderTime)}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <ThemedTimePicker
              value={reminderTime}
              onChange={(e) => setReminderPart(reminderDate, e.target.value)}
              step={300}
              height={42}
              width={140}
            />
          </Box>

          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 2.5, height: 42 }}
            onClick={handleAddComment}
          >
            Submit
          </Button>
        </Box>
      </Modal>

      <LeadTransferModal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        lead={transferLead}
        currentProjectId={projectId}
        onSuccess={fetchLeads}
        role={role}
      />

      <ErrorModal
        open={msgOpen}
        handleClose={() => setMsgOpen(false)}
        msg={msgText}
        type={msgType}
      />
    </div>
    </ThemeProvider>
  );
};

export default LeadTable;

/* ======================================================================
   Quick view drawer
   ====================================================================== */

// History payload shape isn't fixed yet — read the common field names.
const histDate = (h) =>
  h?.createdDate || h?.updatedDate || h?.date || h?.lastFollowUp || h?.followUpDate || h?.timestamp || null;
const histBy = (h) =>
  h?.username || h?.updatedBy?.name || h?.user?.name || h?.createdBy?.name || h?.createdBy || "";
const histRemark = (h) => h?.remark || h?.comment || h?.description || "";

const normalizeHistory = (data) => {
  const list = Array.isArray(data)
    ? data
    : data?.content || data?.histories || data?.followUpHistories || [];
  return [...list]
    .sort((a, b) => new Date(histDate(b) || 0) - new Date(histDate(a) || 0))
    .slice(0, 5);
};

const LeadQuickView = ({
  open,
  lead,
  onClose,
  isMobile,
  canContact,
  metaGroup,
  accessToken,
  onWhatsApp,
  onReminder,
  onUpdate,
  onOpenFull,
}) => {
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const leadId = lead?.leadId;

  useEffect(() => {
    if (!open || !leadId) return;
    let cancelled = false;
    setHistLoading(true);
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/history?leadId=${leadId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        if (!cancelled) setHistory(normalizeHistory(res.data));
      })
      .catch((err) => {
        console.log(err);
        // Fall back to whatever history came with the row
        if (!cancelled) setHistory(normalizeHistory(lead?.followUpHistories || []));
      })
      .finally(() => {
        if (!cancelled) setHistLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, leadId]);

  if (!lead) return null;

  const budget =
    lead.minBudget || lead.maxBudget
      ? `${formatINR(lead.minBudget || 0)} – ${formatINR(lead.maxBudget || 0)}`
      : "";
  const assigned = (lead.assignedTo || []).map((u) => u.name).filter(Boolean).join(", ");

  const details = [
    ["Project", lead.projectName],
    ["Phone", displayPhone(lead, canContact)],
    ["Email", lead.email],
    ["Next follow up", formatDate(lead.nextFollowUp)],
    ["Last follow up", formatDate(lead.lastFollowUp)],
    ["Requirement", lead.requirement],
    ["Property type", lead.preferredPropertyType],
    ["Budget", budget],
    ["Lead source", lead.leadSource],
    ["Priority", lead.priority],
    ["Lead score", lead.leadScore],
    ["Assigned to", assigned],
  ].filter(([, v]) => v !== null && v !== undefined && v !== "");

  const sectionLabel = {
    fontFamily: FONT,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    color: MUTED,
  };

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: isMobile
            ? { width: "100%", maxHeight: "92vh", borderRadius: "18px 18px 0 0" }
            : { width: 480, maxWidth: "100vw" },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: isMobile ? "auto" : "100%",
          maxHeight: isMobile ? "92vh" : "none",
          fontFamily: FONT,
          color: INK,
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, p: 2.5, pb: 2, borderBottom: `1px solid ${LINE}` }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: 20,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                color: INK,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {lead.name} {lead.isLead ? "⭐" : null}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mt: 1 }}>
              <Chip size="small" label={lead.status || "NEW"} />
              {metaGroup && metaGroup !== "Unknown" && (
                <span style={{ fontSize: 12, fontWeight: 600, color: MUTED }}>{metaGroup}</span>
              )}
              {lead.aiScore !== null && lead.aiScore !== undefined && lead.aiScore !== "" && (
                <AiScoreChip score={lead.aiScore} prefix="AI " />
              )}
            </Box>
          </Box>
          <IconButton aria-label="Close" onClick={onClose} sx={{ color: MUTED, mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              columnGap: 2,
              rowGap: 1.75,
            }}
          >
            {details.map(([label, value]) => {
              const wide = ["Email", "Requirement", "Assigned to", "Project"].includes(label);
              const mono = ["Phone", "Next follow up", "Last follow up", "Budget", "Lead score"].includes(label);
              return (
                <Box key={label} sx={{ gridColumn: wide ? "1 / -1" : "auto", minWidth: 0 }}>
                  <div style={sectionLabel}>{label}</div>
                  <Typography
                    sx={{
                      mt: 0.25,
                      fontFamily: mono ? MONO : FONT,
                      fontSize: mono ? 13 : 13.5,
                      fontWeight: 600,
                      color: INK,
                      wordBreak: "break-word",
                    }}
                  >
                    {String(value)}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ mt: 3 }}>
            <div style={{ ...sectionLabel, marginBottom: 10 }}>Recent activity</div>
            {histLoading ? (
              <>
                <Skeleton variant="rounded" height={54} sx={{ mb: 1, borderRadius: "10px" }} />
                <Skeleton variant="rounded" height={54} sx={{ borderRadius: "10px" }} />
              </>
            ) : history.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: MUTED }}>No activity yet.</Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {history.map((h, i) => (
                  <Box
                    key={h.id || h.historyId || i}
                    sx={{ p: 1.5, background: "#F7F9FC", border: `1px solid ${LINE}`, borderRadius: "10px" }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: INK }}>
                        {h.status || "Update"}
                      </span>
                      <span style={{ fontFamily: MONO, fontSize: 11.5, color: MUTED, whiteSpace: "nowrap" }}>
                        {formatDateTime(histDate(h))}
                      </span>
                    </Box>
                    {histRemark(h) && (
                      <Typography sx={{ mt: 0.5, fontSize: 13, color: INK_2, lineHeight: 1.5, wordBreak: "break-word" }}>
                        {histRemark(h)}
                      </Typography>
                    )}
                    {histBy(h) && (
                      <Typography sx={{ mt: 0.5, fontSize: 11.5, color: MUTED }}>by {String(histBy(h))}</Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: `1px solid ${LINE}`, background: "#FFFFFF" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
            {canContact && <ContactButtons lead={lead} onWhatsApp={onWhatsApp} size={38} />}
            <Tooltip title="Set reminder">
              <IconButton
                aria-label="Set reminder"
                onClick={() => onReminder(lead)}
                sx={{ ...bellBtnSx, width: 38, height: 38 }}
              >
                <NotificationsNoneIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              sx={{ ml: "auto", height: 38 }}
              onClick={() => onUpdate(lead)}
            >
              Update
            </Button>
          </Box>
          <Button
            variant="contained"
            fullWidth
            endIcon={<OpenInNewIcon sx={{ fontSize: 17 }} />}
            sx={{ height: 42 }}
            onClick={() => onOpenFull(lead.leadId)}
          >
            Open full lead
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

/* ======================================================================
   Small shared pieces
   ====================================================================== */

const AiScoreChip = ({ score, prefix = "" }) => {
  if (score === null || score === undefined || score === "") {
    return <Box sx={{ color: "#8A97A8" }}>—</Box>;
  }
  const numeric = Number(score);
  const color = numeric >= 70 ? "#1B6B41" : numeric >= 40 ? "#8A5A08" : "#A6342A";
  const bg = numeric >= 70 ? "#E9F7EE" : numeric >= 40 ? "#FDF4E1" : "#FBEDEB";
  const edge = numeric >= 70 ? "#C4E8D2" : numeric >= 40 ? "#F3E0B4" : "#F2CFCA";
  return (
    <Chip
      label={`${prefix}${numeric}`}
      size="small"
      sx={{
        backgroundColor: bg,
        borderColor: edge,
        color,
        fontFamily: MONO,
        fontWeight: 700,
      }}
    />
  );
};

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ display: "flex", justifyContent: "end" }}>
      <GridToolbarQuickFilter
        debounceMs={500}
        sx={{
          border: "1px solid #D5DEE7",
          borderRadius: "9px",
          padding: "3px 10px",
          "&:hover": { backgroundColor: "#F4F7FA" },
          "& .MuiInputBase-root": { borderRadius: "9px" },
          width: 300,
        }}
      />
    </GridToolbarContainer>
  );
}

const SELECT_ALL = "__SELECT_ALL__";
// "None" option: ticking it shows only leads where that field is empty.
const NONE_OPTION = "__NONE__";

const isBlank = (v) => v === null || v === undefined || v === "";

// true when the lead passes a multi-select filter (with None support)
const noneMatch = (selected, leadValue) => {
  if (!selected || selected.length === 0) return true;
  if (isBlank(leadValue)) return selected.includes(NONE_OPTION);
  return selected.includes(leadValue);
};

const optionLabel = (option) =>
  option === SELECT_ALL ? "Select All" : option === NONE_OPTION ? "None" : String(option);

const MultiSelect = ({ label, options, value, onChange, includeNone = false }) => {
  const safeOptions = options || [];
  const safeValue = value || [];
  const realSelected = safeValue.filter((v) => v !== NONE_OPTION);
  const allSelected =
    safeOptions.length > 0 && realSelected.length === safeOptions.length;

  const handleChange = (event, newValue) => {
    if (newValue.includes(SELECT_ALL)) {
      // Select All toggles the real values; None stays as it was
      const keepNone = safeValue.includes(NONE_OPTION) ? [NONE_OPTION] : [];
      onChange(allSelected ? keepNone : [...keepNone, ...safeOptions]);
      return;
    }
    onChange(newValue);
  };

  const listOptions = [
    ...(safeOptions.length ? [SELECT_ALL] : []),
    ...(includeNone ? [NONE_OPTION] : []),
    ...safeOptions,
  ];

  return (
    <Autocomplete
      multiple
      options={listOptions}
      value={safeValue}
      onChange={handleChange}
      disableCloseOnSelect
      size="small"
      limitTags={2}
      clearIcon={<span style={{ fontSize: 14, color: MUTED }}>✕</span>}
      onClear={() => onChange([])}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(option, val) => option === val}
      renderOption={(props, option, { selected }) => {
        const isSelectAll = option === SELECT_ALL;
        const isNone = option === NONE_OPTION;
        const checked = isSelectAll ? allSelected : selected;
        return (
          <li
            {...props}
            key={isSelectAll ? "select-all" : option}
            style={{
              ...(props.style || {}),
              fontWeight: isSelectAll ? 700 : 500,
              fontStyle: isNone ? "italic" : undefined,
              color: isNone ? MUTED : undefined,
              borderBottom: isSelectAll || isNone ? "1px solid #EDF1F5" : undefined,
            }}
          >
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              style={{ marginRight: 8, padding: 4 }}
              checked={checked}
            />
            {optionLabel(option)}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        allSelected ? (
          <>
            <Chip
              size="small"
              label={`All (${safeOptions.length})`}
              {...getTagProps({ index: tagValue.findIndex((v) => v !== NONE_OPTION) })}
            />
            {safeValue.includes(NONE_OPTION) && (
              <Chip
                size="small"
                label="None"
                {...getTagProps({ index: tagValue.indexOf(NONE_OPTION) })}
              />
            )}
          </>
        ) : (
          tagValue.map((option, index) => (
            <Chip label={optionLabel(option)} size="small" {...getTagProps({ index })} />
          ))
        )
      }
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
};

const RangeLabel = ({ label, value, onChange }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
    <Typography fontSize={12} color="text.secondary" fontWeight={600} sx={{ fontFamily: FONT, color: INK_2 }}>
      {label}
    </Typography>
    <label
      style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", fontFamily: FONT, fontSize: 12, fontStyle: "italic", color: MUTED, userSelect: "none" }}
      title={`Show only leads with no ${label}`}
    >
      <Checkbox
        size="small"
        checked={!!value?.none}
        onChange={(e) => onChange({ ...value, none: e.target.checked })}
        sx={{ p: 0.25, "& svg": { fontSize: 16 } }}
      />
      None
    </label>
  </Box>
);

const NumberRangeFilter = ({ label, value, onChange, min = 0, max = 100 }) => (
  <Box>
    <RangeLabel label={label} value={value} onChange={onChange} />
    <Box sx={{ display: "flex", gap: 1, opacity: value?.none ? 0.45 : 1, pointerEvents: value?.none ? "none" : "auto" }}>
      <TextField
        type="number"
        size="small"
        fullWidth
        placeholder="Min"
        inputProps={{ min, max }}
        value={value?.from ?? ""}
        onChange={(e) => onChange({ ...value, from: e.target.value })}
      />
      <TextField
        type="number"
        size="small"
        fullWidth
        placeholder="Max"
        inputProps={{ min, max }}
        value={value?.to ?? ""}
        onChange={(e) => onChange({ ...value, to: e.target.value })}
      />
    </Box>
  </Box>
);

const DateRangeFilter = ({ label, value, onChange }) => (
  <Box>
    <RangeLabel label={label} value={value} onChange={onChange} />
    <Box sx={{ display: "flex", gap: 1, opacity: value?.none ? 0.45 : 1, pointerEvents: value?.none ? "none" : "auto" }}>
      <ThemedDatePicker
        value={value?.from || ""}
        onChange={(e) => onChange({ ...value, from: e.target.value })}
        max={value?.to || undefined}
        height={40}
        sx={{ flex: 1, minWidth: 0 }}
      />
      <ThemedDatePicker
        value={value?.to || ""}
        onChange={(e) => onChange({ ...value, to: e.target.value })}
        min={value?.from || undefined}
        height={40}
        sx={{ flex: 1, minWidth: 0 }}
      />
    </Box>
  </Box>
);